/**
 * Deterministic PRNG (R-RNG-01): xoshiro128** seeded through splitmix32.
 * State is a tuple of four uint32 values and lives inside GameState.
 */

export type RngState = readonly [number, number, number, number];

function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
    return (z ^ (z >>> 16)) >>> 0;
  };
}

export function seedRng(seed: number): RngState {
  const next = splitmix32(seed);
  const state: [number, number, number, number] = [next(), next(), next(), next()];
  // xoshiro must not start from the all-zero state.
  if (state.every((v) => v === 0)) state[0] = 1;
  return state;
}

const rotl = (x: number, k: number) => ((x << k) | (x >>> (32 - k))) >>> 0;

/** Returns the next uint32 and the advanced state. */
export function nextUint32(state: RngState): [number, RngState] {
  let [s0, s1, s2, s3] = state;
  const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
  const t = (s1 << 9) >>> 0;
  s2 = (s2 ^ s0) >>> 0;
  s3 = (s3 ^ s1) >>> 0;
  s1 = (s1 ^ s2) >>> 0;
  s0 = (s0 ^ s3) >>> 0;
  s2 = (s2 ^ t) >>> 0;
  s3 = rotl(s3, 11);
  return [result, [s0, s1, s2, s3]];
}

/** Mutable cursor over an RngState, used inside a single action. */
export class Rng {
  private s: RngState;

  constructor(state: RngState) {
    this.s = state;
  }

  get state(): RngState {
    return this.s;
  }

  /** Uniform float in [0, 1). */
  float(): number {
    const [v, next] = nextUint32(this.s);
    this.s = next;
    return v / 0x1_0000_0000;
  }

  /** Uniform integer in [0, n). */
  int(n: number): number {
    return Math.floor(this.float() * n);
  }

  shuffle<T>(items: readonly T[]): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      [out[i], out[j]] = [out[j] as T, out[i] as T];
    }
    return out;
  }
}
